import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import {
  FiUsers, FiFileText, FiSearch, FiEye, FiX, FiRefreshCw,
  FiChevronDown, FiChevronUp, FiClock, FiCheckCircle, FiAlertCircle,
  FiPhone, FiMail, FiMapPin, FiBriefcase, FiLogOut, FiLock,
  FiBookOpen, FiEdit3, FiType, FiGrid, FiCpu, FiStar, FiTool, FiDownload, FiTrash2
} from 'react-icons/fi';
import '../styles/pages/AdminDashboard.css';

const ADMIN_PASSWORD = 'medcoop2026';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: '#3b82f6' },
  { value: 'reviewed', label: 'Reviewed', color: '#f59e0b' },
  { value: 'contacted', label: 'Contacted', color: '#8b5cf6' },
  { value: 'hired', label: 'Hired', color: '#10b981' },
  { value: 'rejected', label: 'Rejected', color: '#ef4444' },
];

const QUOTE_STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: '#3b82f6' },
  { value: 'reviewed', label: 'Reviewed', color: '#f59e0b' },
  { value: 'contacted', label: 'Contacted', color: '#8b5cf6' },
  { value: 'approved', label: 'Approved', color: '#10b981' },
  { value: 'declined', label: 'Declined', color: '#ef4444' },
];

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [activeTab, setActiveTab] = useState('field-workers');
  const [applications, setApplications] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailPanel, setDetailPanel] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      sessionStorage.setItem('admin_auth', 'true');
    } else {
      setAuthError('Incorrect password. Please try again.');
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem('admin_auth') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Real-time Firestore listeners — data updates instantly without page reload
  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    setFetchError('');

    // Real-time listener for applications
    const unsubApps = onSnapshot(collection(db, 'applications'), (snapshot) => {
      const apps = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      apps.sort((a, b) => {
        const timeA = a.submittedAt?.toMillis ? a.submittedAt.toMillis() : 0;
        const timeB = b.submittedAt?.toMillis ? b.submittedAt.toMillis() : 0;
        return timeB - timeA;
      });
      setApplications(apps);
      setLoading(false);
    }, (error) => {
      console.error('Applications listener error:', error);
      setFetchError(`Failed to load applications: ${error.message}`);
      setLoading(false);
    });

    // Real-time listener for quotes
    const unsubQuotes = onSnapshot(collection(db, 'quoteRequests'), (snapshot) => {
      const qts = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      qts.sort((a, b) => {
        const timeA = a.submittedAt?.toMillis ? a.submittedAt.toMillis() : 0;
        const timeB = b.submittedAt?.toMillis ? b.submittedAt.toMillis() : 0;
        return timeB - timeA;
      });
      setQuotes(qts);
    }, (error) => {
      console.error('Quotes listener error:', error);
    });

    // Cleanup listeners on unmount or logout
    return () => {
      unsubApps();
      unsubQuotes();
    };
  }, [isAuthenticated]);

  const updateStatus = async (collectionName, docId, newStatus) => {
    try {
      await updateDoc(doc(db, collectionName, docId), { status: newStatus });
      // Real-time listener will auto-update the list
      if (selectedItem?.id === docId) setSelectedItem(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteRecord = async (collectionName, docId, itemName) => {
    const confirmed = window.confirm(`Are you sure you want to delete "${itemName}"?\n\nThis action cannot be undone.`);
    if (!confirmed) return;
    try {
      await deleteDoc(doc(db, collectionName, docId));
      // Real-time listener will auto-update the list
      if (selectedItem?.id === docId) closeDetail();
    } catch (error) {
      console.error('Error deleting record:', error);
      alert('Failed to delete: ' + error.message);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_auth');
    setPassword('');
  };

  const openDetail = (item) => {
    setSelectedItem(item);
    setDetailPanel(true);
    setExpandedSections({});
  };

  const closeDetail = () => {
    setDetailPanel(false);
    setTimeout(() => setSelectedItem(null), 300);
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  // Separate field workers from office staff
  const fieldWorkerApps = applications.filter(a => a.applicationType === 'field-worker');
  const officeStaffApps = applications.filter(a => a.applicationType === 'office-staff');

  // Filter logic based on active tab
  const getFilteredItems = () => {
    let items = [];
    if (activeTab === 'field-workers') items = fieldWorkerApps;
    else if (activeTab === 'office-staff') items = officeStaffApps;
    else if (activeTab === 'quotes') return quotes.filter(q => {
      const matchSearch = !searchTerm ||
        (q.companyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (q.contactPerson || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (q.email || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = filterStatus === 'all' || q.status === filterStatus;
      return matchSearch && matchStatus;
    });

    return items.filter(app => {
      const fd = app.formData || {};
      const name = (fd.fullName || fd.firstName || '').toLowerCase();
      const email = (fd.emailAddress || '').toLowerCase();
      const position = (fd.positionApplied || fd.preferredPosition || '').toLowerCase();
      const matchSearch = !searchTerm || name.includes(searchTerm.toLowerCase()) || email.includes(searchTerm.toLowerCase()) || position.includes(searchTerm.toLowerCase());
      const matchStatus = filterStatus === 'all' || app.status === filterStatus;
      return matchSearch && matchStatus;
    });
  };

  const filteredItems = getFilteredItems();

  const stats = {
    fieldWorkers: fieldWorkerApps.length,
    newFieldWorkers: fieldWorkerApps.filter(a => a.status === 'new').length,
    officeStaff: officeStaffApps.length,
    newOfficeStaff: officeStaffApps.filter(a => a.status === 'new').length,
    totalQuotes: quotes.length,
    newQuotes: quotes.filter(q => q.status === 'new').length,
  };

  const getStatusBadge = (status, isQuote = false) => {
    const options = isQuote ? QUOTE_STATUS_OPTIONS : STATUS_OPTIONS;
    const opt = options.find(o => o.value === status) || options[0];
    return <span className="status-badge" style={{ background: opt.color }}>{opt.label}</span>;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) return timestamp.toDate().toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    return new Date(timestamp).toLocaleDateString('en-PH');
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case 'field-workers': return 'Field Service Workers';
      case 'office-staff': return 'Office Staff';
      case 'quotes': return 'Quote Requests';
      default: return 'Dashboard';
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="admin-login-page">
        <div className="admin-login-card">
          <div className="login-logo">
            <FiLock />
          </div>
          <h1>MEDCoop Admin</h1>
          <p>Enter your admin password to continue</p>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              autoFocus
            />
            {authError && <div className="login-error"><FiAlertCircle /> {authError}</div>}
            <button type="submit" className="login-btn">Sign In</button>
          </form>
        </div>
      </div>
    );
  }

  // Detail Panel - Application
  const renderApplicationDetail = () => {
    if (!selectedItem) return null;
    const fd = selectedItem.formData || {};
    const isOffice = selectedItem.applicationType === 'office-staff';

    return (
      <div className="detail-content">
        <div className="detail-header-info">
          <h2>{fd.fullName || `${fd.firstName || ''} ${fd.lastName || ''}`.trim() || 'Unknown'}</h2>
          <p className="detail-subtitle">{isOffice ? 'Office Staff' : 'Field Service Worker'} — {fd.positionApplied || fd.preferredPosition || 'N/A'}</p>
          <div className="detail-meta">
            {getStatusBadge(selectedItem.status)}
            <span className="detail-id">{selectedItem.applicationId}</span>
            <span className="detail-date"><FiClock /> {selectedItem.submittedAtLocal || formatDate(selectedItem.submittedAt)}</span>
          </div>
          <div className="status-changer">
            <label>Status:</label>
            <select value={selectedItem.status || 'new'} onChange={(e) => updateStatus('applications', selectedItem.id, e.target.value)}>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* Personal Info */}
        <div className="detail-section">
          <div className="detail-section-header" onClick={() => toggleSection('personal')}>
            <h3><FiUsers /> Personal Information</h3>
            {expandedSections.personal ? <FiChevronUp /> : <FiChevronDown />}
          </div>
          {expandedSections.personal !== false && (
            <div className="detail-section-body">
              <div className="detail-grid">
                <div className="detail-field"><label>Full Name</label><span>{fd.fullName || 'N/A'}</span></div>
                <div className="detail-field"><label>Age</label><span>{fd.age || 'N/A'}</span></div>
                <div className="detail-field"><label>Sex</label><span>{fd.sex || 'N/A'}</span></div>
                <div className="detail-field"><label>Civil Status</label><span>{fd.civilStatus || 'N/A'}</span></div>
                <div className="detail-field"><label>Birthdate</label><span>{fd.birthDate || 'N/A'}</span></div>
                <div className="detail-field"><label>Birthplace</label><span>{fd.birthPlace || 'N/A'}</span></div>
              </div>
            </div>
          )}
        </div>

        {/* Contact */}
        <div className="detail-section">
          <div className="detail-section-header" onClick={() => toggleSection('contact')}>
            <h3><FiPhone /> Contact & Address</h3>
            {expandedSections.contact ? <FiChevronUp /> : <FiChevronDown />}
          </div>
          {expandedSections.contact !== false && (
            <div className="detail-section-body">
              <div className="detail-grid">
                <div className="detail-field"><label><FiPhone /> Contact</label><span>{fd.contactNumber || 'N/A'}</span></div>
                <div className="detail-field"><label><FiMail /> Email</label><span>{fd.emailAddress || 'N/A'}</span></div>
                <div className="detail-field full"><label><FiMapPin /> Address</label><span>{fd.address || [fd.addressLine1, fd.barangay, fd.cityMunicipality, fd.province, fd.postalCode].filter(Boolean).join(', ') || 'N/A'}</span></div>
              </div>
            </div>
          )}
        </div>

        {/* Position / Job */}
        <div className="detail-section">
          <div className="detail-section-header" onClick={() => toggleSection('job')}>
            <h3><FiBriefcase /> Position</h3>
            {expandedSections.job ? <FiChevronUp /> : <FiChevronDown />}
          </div>
          {expandedSections.job !== false && (
            <div className="detail-section-body">
              <div className="detail-grid">
                <div className="detail-field"><label>Position</label><span>{fd.positionApplied || fd.preferredPosition || 'N/A'}</span></div>
                {fd.preferredLocation && <div className="detail-field"><label>Location</label><span>{fd.preferredLocation}</span></div>}
                {fd.otherService && <div className="detail-field"><label>Other Service</label><span>{fd.otherService}</span></div>}
                <div className="detail-field"><label>Resume</label><span>{fd.resumeFileName || 'Not attached'}</span></div>
              </div>
            </div>
          )}
        </div>

        {/* Field Worker - Government IDs & Emergency Contact & Education */}
        {!isOffice && (
          <>
            <div className="detail-section">
              <div className="detail-section-header" onClick={() => toggleSection('govIds')}>
                <h3><FiFileText /> Government IDs</h3>
                {expandedSections.govIds ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {expandedSections.govIds && (
                <div className="detail-section-body">
                  <div className="detail-grid">
                    <div className="detail-field"><label>SSS No.</label><span>{fd.sssNo || 'N/A'}</span></div>
                    <div className="detail-field"><label>PhilHealth No.</label><span>{fd.philhealthNo || 'N/A'}</span></div>
                    <div className="detail-field"><label>Pag-IBIG No.</label><span>{fd.pagibigNo || 'N/A'}</span></div>
                    <div className="detail-field"><label>TIN No.</label><span>{fd.tinNo || 'N/A'}</span></div>
                    <div className="detail-field"><label>Driver's License</label><span>{fd.driversLicense || 'N/A'}</span></div>
                  </div>
                </div>
              )}
            </div>

            <div className="detail-section">
              <div className="detail-section-header" onClick={() => toggleSection('emergency')}>
                <h3><FiPhone /> Emergency Contact</h3>
                {expandedSections.emergency ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {expandedSections.emergency && (
                <div className="detail-section-body">
                  <div className="detail-grid">
                    <div className="detail-field"><label>Name</label><span>{fd.emergencyName || 'N/A'}</span></div>
                    <div className="detail-field"><label>Relation</label><span>{fd.emergencyRelation || 'N/A'}</span></div>
                    <div className="detail-field full"><label>Address</label><span>{fd.emergencyAddress || 'N/A'}</span></div>
                    <div className="detail-field"><label>Contact</label><span>{fd.emergencyContact || 'N/A'}</span></div>
                  </div>
                </div>
              )}
            </div>

            <div className="detail-section">
              <div className="detail-section-header" onClick={() => toggleSection('education')}>
                <h3><FiBookOpen /> Education</h3>
                {expandedSections.education ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {expandedSections.education && (
                <div className="detail-section-body">
                  <div className="detail-grid">
                    <div className="detail-field"><label>Elementary</label><span>{fd.elementarySchool || 'N/A'} ({fd.elementaryYear || ''})</span></div>
                    <div className="detail-field"><label>High School</label><span>{fd.highschoolSchool || 'N/A'} ({fd.highschoolYear || ''})</span></div>
                    <div className="detail-field"><label>College</label><span>{fd.collegeSchool || 'N/A'} - {fd.collegeCourse || ''} ({fd.collegeYear || ''})</span></div>
                    <div className="detail-field full"><label>Special Skills</label><span>{fd.specialSkills || 'N/A'}</span></div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Test Results (Office Staff Only) */}
        {isOffice && (
          <>
            <div className="detail-section">
              <div className="detail-section-header" onClick={() => toggleSection('typing')}>
                <h3><FiType /> Typing Test</h3>
                {expandedSections.typing ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {expandedSections.typing && (
                <div className="detail-section-body">
                  <div className="test-summary-badge">{fd.typingTestResults || 'Not completed'}</div>
                  {fd.typingTestDetails && (
                    <div className="detail-grid">
                      <div className="detail-field"><label>Net WPM</label><span className="score">{fd.typingTestDetails.netWpm}</span></div>
                      <div className="detail-field"><label>Gross WPM</label><span>{fd.typingTestDetails.grossWpm}</span></div>
                      <div className="detail-field"><label>Accuracy</label><span className="score">{fd.typingTestDetails.accuracy}%</span></div>
                      <div className="detail-field"><label>Characters</label><span>{fd.typingTestDetails.totalChars}</span></div>
                      <div className="detail-field"><label>Errors</label><span>{fd.typingTestDetails.errors}</span></div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="detail-section">
              <div className="detail-section-header" onClick={() => toggleSection('iq')}>
                <h3><FiCpu /> IQ Test</h3>
                {expandedSections.iq ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {expandedSections.iq && (
                <div className="detail-section-body">
                  <div className="test-summary-badge">{fd.iqTestScore || 'Not taken'}</div>
                  {fd.iqTestDetails?.questions && (
                    <div className="test-answers-list">
                      {fd.iqTestDetails.questions.map((q, i) => {
                        const opts = q.options || [];
                        const answerText = opts[q.userAnswer] || (q.userAnswer != null ? `Option ${String.fromCharCode(65 + q.userAnswer)}` : 'Skipped');
                        const correctText = opts[q.correct] || `Option ${String.fromCharCode(65 + q.correct)}`;
                        const isCorrect = q.userAnswer === q.correct;
                        return (
                          <div key={i} className={`test-answer-item ${isCorrect ? 'correct' : 'incorrect'}`}>
                            <span className="q-num">Q{i + 1}</span>
                            <span className="q-text">{q.text || `Question ${q.id}`}</span>
                            <span className="q-answer">Answer: {answerText}</span>
                            <span className="q-correct">Correct: {correctText}</span>
                            <span className={`q-icon ${isCorrect ? 'pass' : 'fail'}`}>
                              {isCorrect ? <FiCheckCircle /> : <FiAlertCircle />}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="detail-section">
              <div className="detail-section-header" onClick={() => toggleSection('abstract')}>
                <h3><FiGrid /> Abstract Reasoning</h3>
                {expandedSections.abstract ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {expandedSections.abstract && (
                <div className="detail-section-body">
                  <div className="test-summary-badge">{fd.abstractReasoningScore || 'Not taken'}</div>
                  {fd.abstractTestDetails?.questions && (
                    <div className="test-answers-list">
                      {fd.abstractTestDetails.questions.map((q, i) => {
                        const opts = q.options || [];
                        const answerText = opts[q.userAnswer] || (q.userAnswer != null ? `Option ${String.fromCharCode(65 + q.userAnswer)}` : 'Skipped');
                        const correctText = opts[q.correct] || `Option ${String.fromCharCode(65 + q.correct)}`;
                        const isCorrect = q.userAnswer === q.correct;
                        return (
                          <div key={i} className={`test-answer-item ${isCorrect ? 'correct' : 'incorrect'}`}>
                            <span className="q-num">Q{i + 1}</span>
                            <span className="q-text">Question {q.id}</span>
                            <span className="q-answer">Answer: {answerText}</span>
                            <span className="q-correct">Correct: {correctText}</span>
                            <span className={`q-icon ${isCorrect ? 'pass' : 'fail'}`}>
                              {isCorrect ? <FiCheckCircle /> : <FiAlertCircle />}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="detail-section">
              <div className="detail-section-header" onClick={() => toggleSection('math')}>
                <h3><FiBookOpen /> Math Test</h3>
                {expandedSections.math ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {expandedSections.math && (
                <div className="detail-section-body">
                  <div className="test-summary-badge">{fd.mathTestScore || 'Not taken'}</div>
                  {fd.mathTestDetails && (
                    <div className="detail-grid">
                      <div className="detail-field"><label>Correct</label><span className="score">{fd.mathTestDetails.correct}</span></div>
                      <div className="detail-field"><label>Total</label><span>{fd.mathTestDetails.total}</span></div>
                      <div className="detail-field"><label>Percentage</label><span className="score">{fd.mathTestDetails.percentage}%</span></div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="detail-section">
              <div className="detail-section-header" onClick={() => toggleSection('personality')}>
                <h3><FiStar /> Personality Test</h3>
                {expandedSections.personality ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {expandedSections.personality && (
                <div className="detail-section-body">
                  <div className="test-summary-badge">{fd.personalityTestResults || 'Not completed'}</div>
                  {fd.personalityTestDetails && (() => {
                    // Parse personality data — extract factor results
                    const details = fd.personalityTestDetails;
                    const factors = Object.entries(details)
                      .filter(([key]) => !['strongestTraits', 'weakestTraits'].includes(key))
                      .map(([key, data]) => ({ key, ...data }))
                      .sort((a, b) => (b.percentage || 0) - (a.percentage || 0));
                    
                    // Parse strongest/weakest traits (may be JSON strings from nested array conversion)
                    let strongest = details.strongestTraits;
                    let weakest = details.weakestTraits;
                    if (typeof strongest === 'string') try { strongest = JSON.parse(strongest); } catch(e) { strongest = null; }
                    if (typeof weakest === 'string') try { weakest = JSON.parse(weakest); } catch(e) { weakest = null; }

                    return (
                      <div className="personality-results">
                        {/* Strongest Traits */}
                        {strongest && Array.isArray(strongest) && strongest.length > 0 && (
                          <div className="personality-summary">
                            <h4 style={{color: '#10b981', marginBottom: '8px'}}>✦ Strongest Traits</h4>
                            <div className="trait-tags">
                              {strongest.map((item, i) => {
                                const name = item?.[1]?.factorInfo?.name || item?.factorInfo?.name || `Trait ${i+1}`;
                                const pct = item?.[1]?.percentage || item?.percentage || 0;
                                return <span key={i} className="trait-tag strong">{name} ({pct}%)</span>;
                              })}
                            </div>
                          </div>
                        )}
                        {weakest && Array.isArray(weakest) && weakest.length > 0 && (
                          <div className="personality-summary" style={{marginTop: '12px'}}>
                            <h4 style={{color: '#ef4444', marginBottom: '8px'}}>✦ Areas for Growth</h4>
                            <div className="trait-tags">
                              {weakest.map((item, i) => {
                                const name = item?.[1]?.factorInfo?.name || item?.factorInfo?.name || `Trait ${i+1}`;
                                const pct = item?.[1]?.percentage || item?.percentage || 0;
                                return <span key={i} className="trait-tag weak">{name} ({pct}%)</span>;
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* All Factors */}
                        <div style={{marginTop: '20px'}}>
                          {factors.map((f) => {
                            const info = f.factorInfo || {};
                            const pct = f.percentage || 0;
                            const label = f.isHigh ? info.highLabel : info.lowLabel;
                            return (
                              <div key={f.key} className="personality-trait">
                                <div className="trait-header">
                                  <span className="trait-name">{info.name || f.key}</span>
                                  <span className="trait-label" style={{color: info.color || '#666'}}>{label || ''}</span>
                                  <span className="trait-score">Sten {f.stenScore} · {pct}%</span>
                                </div>
                                <div className="trait-bar">
                                  <div className="trait-fill" style={{ width: `${pct}%`, background: info.color || '#3b82f6' }}></div>
                                </div>
                                <p className="trait-desc">{f.isHigh ? info.highDescription : info.lowDescription}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="detail-section">
              <div className="detail-section-header" onClick={() => toggleSection('essay')}>
                <h3><FiEdit3 /> Essay</h3>
                {expandedSections.essay ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {expandedSections.essay && (
                <div className="detail-section-body">
                  <div className="test-summary-badge">{fd.essayWordCount ? `${fd.essayWordCount} words` : 'Not written'}</div>
                  {fd.essayContent && (
                    <div className="essay-content">{fd.essayContent}</div>
                  )}
                </div>
              )}
            </div>

            <div className="detail-section">
              <div className="detail-section-header" onClick={() => toggleSection('documents')}>
                <h3><FiFileText /> Documents</h3>
                {expandedSections.documents ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              {expandedSections.documents && (
                <div className="detail-section-body">
                  <div className="detail-grid">
                    <div className="detail-field">
                      <label>Resume</label>
                      <span>{fd.resumeFileName || 'No resume attached'}</span>
                      {fd.resumeFileData && (
                        <a href={fd.resumeFileData.data} download={fd.resumeFileData.name} className="download-link">
                          <FiDownload /> Download ({(fd.resumeFileData.size / 1024).toFixed(0)} KB)
                        </a>
                      )}
                      {!fd.resumeFileData && fd.resumeFileName && fd.resumeFileName !== 'No resume attached' && (
                        <span className="file-note">File too large or not stored</span>
                      )}
                    </div>
                    <div className="detail-field">
                      <label>Excel Test File</label>
                      <span>{fd.excelFileName || 'No file uploaded'}</span>
                      {fd.excelFileData && (
                        <a href={fd.excelFileData.data} download={fd.excelFileData.name} className="download-link">
                          <FiDownload /> Download ({(fd.excelFileData.size / 1024).toFixed(0)} KB)
                        </a>
                      )}
                      {!fd.excelFileData && fd.excelFileName && fd.excelFileName !== 'No file uploaded' && (
                        <span className="file-note">File too large or not stored</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // Detail Panel - Quote
  const renderQuoteDetail = () => {
    if (!selectedItem) return null;
    return (
      <div className="detail-content">
        <div className="detail-header-info">
          <h2>{selectedItem.companyName || 'Unknown Company'}</h2>
          <p className="detail-subtitle">{selectedItem.serviceNeeded || 'N/A'} — {selectedItem.numberOfWorkers || 0} workers</p>
          <div className="detail-meta">
            {getStatusBadge(selectedItem.status, true)}
            <span className="detail-id">{selectedItem.quoteId}</span>
            <span className="detail-date"><FiClock /> {selectedItem.submittedAtLocal || formatDate(selectedItem.submittedAt)}</span>
          </div>
          <div className="status-changer">
            <label>Status:</label>
            <select value={selectedItem.status || 'new'} onChange={(e) => updateStatus('quoteRequests', selectedItem.id, e.target.value)}>
              {QUOTE_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div className="detail-section">
          <div className="detail-section-header"><h3><FiBriefcase /> Company & Service</h3></div>
          <div className="detail-section-body">
            <div className="detail-grid">
              <div className="detail-field"><label>Company</label><span>{selectedItem.companyName}</span></div>
              <div className="detail-field"><label>Service</label><span>{selectedItem.serviceNeeded}</span></div>
              <div className="detail-field"><label>Region</label><span>{selectedItem.region}</span></div>
              <div className="detail-field"><label>Branch</label><span>{selectedItem.branchName}</span></div>
              <div className="detail-field"><label>Workers</label><span className="score">{selectedItem.numberOfWorkers}</span></div>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <div className="detail-section-header"><h3><FiUsers /> Contact Person</h3></div>
          <div className="detail-section-body">
            <div className="detail-grid">
              <div className="detail-field"><label>Name</label><span>{selectedItem.contactPerson}</span></div>
              <div className="detail-field"><label>Position</label><span>{selectedItem.position}</span></div>
              <div className="detail-field"><label><FiMail /> Email</label><span>{selectedItem.email}</span></div>
              <div className="detail-field"><label><FiPhone /> Phone</label><span>{selectedItem.phone}</span></div>
              <div className="detail-field full"><label><FiMapPin /> Company Address</label><span>{selectedItem.companyAddress}</span></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render application table row
  const renderAppRow = (app) => {
    const fd = app.formData || {};
    const name = fd.fullName || `${fd.firstName || ''} ${fd.lastName || ''}`.trim() || 'N/A';
    return (
      <tr key={app.id} onClick={() => openDetail(app)}>
        <td className="id-cell">{app.applicationId || 'N/A'}</td>
        <td className="name-cell">
          <strong>{name}</strong>
          <small>{fd.emailAddress || ''}</small>
        </td>
        <td>{fd.positionApplied || fd.preferredPosition || 'N/A'}</td>
        <td>{fd.contactNumber || 'N/A'}</td>
        <td className="date-cell">{app.submittedAtLocal || formatDate(app.submittedAt)}</td>
        <td>{getStatusBadge(app.status)}</td>
        <td className="action-cell">
          <button className="view-btn" onClick={(e) => { e.stopPropagation(); openDetail(app); }}><FiEye /></button>
          <button className="delete-btn" onClick={(e) => { e.stopPropagation(); deleteRecord('applications', app.id, name); }}><FiTrash2 /></button>
        </td>
      </tr>
    );
  };

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-brand">
          <h2>MEDCoop</h2>
          <span>Admin Panel</span>
        </div>
        <nav className="sidebar-nav">
          <div className="sidebar-section-label">Applications</div>
          <button className={`sidebar-btn ${activeTab === 'field-workers' ? 'active' : ''}`} onClick={() => { setActiveTab('field-workers'); setFilterStatus('all'); setSearchTerm(''); }}>
            <FiTool /> <span>Field Service Workers</span>
            {stats.newFieldWorkers > 0 && <span className="nav-badge">{stats.newFieldWorkers}</span>}
          </button>
          <button className={`sidebar-btn ${activeTab === 'office-staff' ? 'active' : ''}`} onClick={() => { setActiveTab('office-staff'); setFilterStatus('all'); setSearchTerm(''); }}>
            <FiUsers /> <span>Office Staff</span>
            {stats.newOfficeStaff > 0 && <span className="nav-badge">{stats.newOfficeStaff}</span>}
          </button>
          <div className="sidebar-section-label">Quotes</div>
          <button className={`sidebar-btn ${activeTab === 'quotes' ? 'active' : ''}`} onClick={() => { setActiveTab('quotes'); setFilterStatus('all'); setSearchTerm(''); }}>
            <FiFileText /> <span>Quote Requests</span>
            {stats.newQuotes > 0 && <span className="nav-badge">{stats.newQuotes}</span>}
          </button>
        </nav>
        <button className="sidebar-btn logout-btn" onClick={handleLogout}>
          <FiLogOut /> <span>Logout</span>
        </button>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="topbar-left">
            <h1>{getTabTitle()}</h1>
            <span className="topbar-count">{filteredItems.length} records</span>
          </div>
          <div className="topbar-right">
            <span className="live-badge"><span className="live-dot"></span> Live</span>
          </div>
        </header>

        {/* Stats */}
        <div className="stats-row">
          <div className={`stat-card ${activeTab === 'field-workers' ? 'highlighted' : ''}`}>
            <div className="stat-icon apps"><FiTool /></div>
            <div className="stat-info"><span className="stat-number">{stats.fieldWorkers}</span><span className="stat-label">Field Workers</span></div>
          </div>
          <div className={`stat-card ${activeTab === 'office-staff' ? 'highlighted' : ''}`}>
            <div className="stat-icon new"><FiUsers /></div>
            <div className="stat-info"><span className="stat-number">{stats.officeStaff}</span><span className="stat-label">Office Staff</span></div>
          </div>
          <div className={`stat-card ${activeTab === 'quotes' ? 'highlighted' : ''}`}>
            <div className="stat-icon quotes"><FiFileText /></div>
            <div className="stat-info"><span className="stat-number">{stats.totalQuotes}</span><span className="stat-label">Quote Requests</span></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon new-q"><FiAlertCircle /></div>
            <div className="stat-info"><span className="stat-number">{stats.newFieldWorkers + stats.newOfficeStaff + stats.newQuotes}</span><span className="stat-label">Total New</span></div>
          </div>
        </div>

        {fetchError && (
          <div className="fetch-error">
            <FiAlertCircle /> {fetchError}
          </div>
        )}

        {/* Filters */}
        <div className="filters-bar">
          <div className="search-box">
            <FiSearch />
            <input type="text" placeholder={activeTab === 'quotes' ? 'Search by company, contact, email...' : 'Search by name, email, position...'} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            {(activeTab === 'quotes' ? QUOTE_STATUS_OPTIONS : STATUS_OPTIONS).map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Data Table */}
        <div className="data-table-container">
          {loading ? (
            <div className="loading-state"><FiRefreshCw className="spinning" /> Loading data...</div>
          ) : activeTab === 'quotes' ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Company</th>
                  <th>Service</th>
                  <th>Branch</th>
                  <th>Workers</th>
                  <th>Contact</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr><td colSpan="9" className="empty-state">No quote requests found</td></tr>
                ) : filteredItems.map(q => (
                  <tr key={q.id} onClick={() => openDetail(q)}>
                    <td className="id-cell">{q.quoteId || 'N/A'}</td>
                    <td className="name-cell">
                      <strong>{q.companyName || 'N/A'}</strong>
                      <small>{q.contactPerson || ''}</small>
                    </td>
                    <td>{q.serviceNeeded || 'N/A'}</td>
                    <td>{q.branchName || 'N/A'}</td>
                    <td className="workers-cell">{q.numberOfWorkers || 0}</td>
                    <td>{q.email || 'N/A'}</td>
                    <td className="date-cell">{q.submittedAtLocal || formatDate(q.submittedAt)}</td>
                    <td>{getStatusBadge(q.status, true)}</td>
                    <td className="action-cell">
                      <button className="view-btn" onClick={(e) => { e.stopPropagation(); openDetail(q); }}><FiEye /></button>
                      <button className="delete-btn" onClick={(e) => { e.stopPropagation(); deleteRecord('quoteRequests', q.id, q.companyName || 'Quote'); }}><FiTrash2 /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Applicant</th>
                  <th>Position</th>
                  <th>Contact</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr><td colSpan="7" className="empty-state">No {activeTab === 'field-workers' ? 'field worker' : 'office staff'} applications found</td></tr>
                ) : filteredItems.map(renderAppRow)}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Detail Panel */}
      {(detailPanel || selectedItem) && (
        <>
          <div className={`detail-overlay ${detailPanel ? 'open' : ''}`} onClick={closeDetail}></div>
          <div className={`detail-panel ${detailPanel ? 'open' : ''}`}>
            <div className="detail-panel-header">
              <h2>{activeTab === 'quotes' ? 'Quote Details' : 'Application Details'}</h2>
              <button className="close-panel-btn" onClick={closeDetail}><FiX /></button>
            </div>
            <div className="detail-panel-body">
              {activeTab === 'quotes' ? renderQuoteDetail() : renderApplicationDetail()}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
